import store from 'store2';
import memoizeOne from 'memoize-one';
import jwtDecode from 'jwt-decode';
import io from 'socket.io-client';
import uuid from 'uuid/v1';

import i18n from '@mssgme/i18n';
import { ApiClient } from '@mssgme/api-client';

import { ACCESS_TOKEN_KEY, PATHS } from './constants';
import media from './media';

const memJwtDecode = memoizeOne(jwtDecode);

/** @typedef {import('@mssgme/api-client').ApiGetMethod} ApiGetMethod */
/** @typedef {import('@mssgme/api-client').ApiPutMethod} ApiPutMethod */
/** @typedef {import('@mssgme/api-client').ApiDeleteMethod} ApiDeleteMethod */

/** @typedef {{ new: number; processing: number; rejected: number; accepted: number }} FeedbackCounts */
/**
 * @template {{ payload?: object, query?: object }} O
 * @typedef {(options: O) => Promise<{ token: string }>} TokenRequestMethod<O>
 */

export class MssgApiClient extends ApiClient {
    _params = {
        workspaceId: '',
    };

    _socket = null;

    /**
     * @param {{ baseUrl?: string, socketUrl: string, headers: object, middlewares: Function[] }} opts Client options
     */
    constructor(opts = {}) {
        super(opts);
        this._socketUrl = opts.socketUrl;

        this._accessToken = store.get(ACCESS_TOKEN_KEY);
        this._storeClientId();
    }

    // TODO: add clientId to JWT
    _storeClientId() {
        let clientId = store.get('client_uuid');
        if (!clientId) {
            clientId = uuid();
            store.set('client_uuid', clientId);
        }
        this.clientId = clientId;
    }

    addHeaders(request) {
        if (this._accessToken) {
            request.headers['Authorization'] = `Bearer "${this._accessToken}"`;
        }

        request.headers['Accept-Language'] = i18n.language;
        request.headers['X-Client-Id'] = this.clientId;
    }

    get isAuthenticated() {
        return !!this._accessToken;
    }

    get accessToken() {
        return this._accessToken;
    }

    set accessToken(token) {
        this._accessToken = token;

        if (token) {
            store.set(ACCESS_TOKEN_KEY, token);
        } else {
            store.remove(ACCESS_TOKEN_KEY);
            this._closeSocket();
        }

        this.emit('changeAccessToken');
    }

    get aboutMe() {
        return this._accessToken ? memJwtDecode(this._accessToken) : null;
    }

    _createSocket() {
        const socket = io(this._socketUrl, {
            transports: __DEV__ ? ['polling', 'websocket'] : ['websocket'],
            query: {
                token: this._accessToken,
                clientId: this.clientId,
            },
        });

        socket.on('connect', () => {
            socket.emit('start');
        });

        return socket;
    }

    _closeSocket() {
        if (this._socket) {
            this._socket.close();
            this._socket = null;
        }
    }

    createSocket() {
        if (!this._socket) this._socket = this._createSocket();
        return this._socket;
    }

    async execRequest(...args) {
        try {
            return await super.execRequest(...args);
        } catch (e) {
            // 403 Forbidden (Unauthorized), and not a blocked feature request, just a regular request
            if (e.status === 403 && !e.permission) {
                this.accessToken = null;
            }

            throw e;
        }
    }

    getWorkspaceId() {
        return this._params.workspaceId;
    }

    /**
     * @param {'GET'|'POST'} method Request method
     * @param {string} path Request path
     * @returns {TokenRequestMethod<O>} Response
     */
    tokenRequest(method, path) {
        return async (options) => {
            const res = await this.apiCall(method, path, options);
            this.accessToken = res.token;
            return res;
        };
    }

    // resources

    resources = {
        auth: {
            /** @member {TokenRequestMethod<{ code: string }>} */
            getToken: this.tokenRequest('GET', `${PATHS.auth}/token/:code`),
            /** @member {TokenRequestMethod<{ payload: { username: string; password: string } }>} */
            login: this.tokenRequest('POST', `${PATHS.auth}/login`),
            logout: async () => {
                this.accessToken = null;

                return await this.apiCall('GET', `${PATHS.auth}/logout`);
            },
        },

        billing: {
            update: this.put(PATHS.billing) /** TODO: remove */,
            changePlan: this.post(`${PATHS.billing}/change-plan`),
            previewPlanChange: this.post(`${PATHS.billing}/preview-plan-change`),
            changeCreditCard: this.put(`${PATHS.billing}/card`) /** TODO: remove */,
            applyCoupon: this.put(`${PATHS.billing}/apply-coupon`) /** TODO: remove */,
            getCoupon: this.get(`${PATHS.billing}/coupons/:couponId`) /** TODO: remove */,
            details: this.get(`${PATHS.billing}/details`),
            subscription: {
                create: this.post(`${PATHS.subscription}/create`),
                cancel: this.post(`${PATHS.subscription}/cancel`),
                pause: this.post(`${PATHS.subscription}/pause`), // TODO: remove
                renew: this.post(`${PATHS.subscription}/renew`),
                changeCard: this.post(`${PATHS.subscription}/change-card`),
                payNow: this.post(`${PATHS.subscription}/pay-now`),
            },
            products: {
                prices: this.get(`${PATHS.billing}/products/prices`),
            },
        },

        bot: {
            donate: this.post(`${PATHS.bot}/donate`),
            subscribe: this.post(`${PATHS.bot}/subscribe`),
        },

        bots: {
            create: this.post(`${PATHS.bots}`),
            update: this.put(`${PATHS.bots}/:botId`),
            delete: this.delete(`${PATHS.bots}/:botId`),
            upload: async ({ payload, botId, field = 'file' }) => {
                const form = new FormData();
                form.append(field, payload);

                return this.apiCall('POST', `${PATHS.bots}/:botId/upload`, {
                    botId,
                    payload: form,
                    query: { field },
                });
            },
        },

        card: {
            update: this.put(`${PATHS.card}`),
            patch: this.patch(`${PATHS.card}`),
            statistics: this.get(`${PATHS.card}/statistics`),
            // updateCategory: this.put(`${PATHS.card}/category`),
            // updateUrl: this.put(`${PATHS.card}/url`),
            // updateName: this.put(`${PATHS.card}/name`),
            // updateDescription: this.put(`${PATHS.card}/description`),
            // updateAvatar: this.put(`${PATHS.card}/photo`),
            // updateCover: this.put(`${PATHS.card}/cover`),
            messengers: {
                create: this.post(`${PATHS.card}/messengers`),
                sort: this.put(`${PATHS.card}/messengers/sort`),
                update: this.put(`${PATHS.card}/messengers/:messengerId`),
                delete: this.delete(`${PATHS.card}/messengers/:messengerId`),
            },
            links: {
                /** @member {ApiPutMethod<{}, LinkInterface>} create */
                create: this.post(`${PATHS.card}/links`),
                /** @member {ApiPutMethod<{ linkId: string }, LinkInterface>} */
                update: this.put(`${PATHS.card}/links/:linkId`),
                /** @member {ApiDeleteMethod<{ linkId: string }, LinkInterface>} */
                delete: this.delete(`${PATHS.card}/links/:linkId`),
            },
            domain: {
                connect: this.put(`${PATHS.card}/domain`),
                disconnect: this.delete(`${PATHS.card}/domain`),
            },
            uploadAvatar: ({ payload }) => {
                return this.resources.workspaces.upload({
                    payload,
                    uploadType: 'photo',
                    field: 'design.photo',
                });
            },
            uploadCover: ({ payload }) => {
                return this.resources.workspaces.upload({
                    payload,
                    uploadType: 'background',
                    field: 'design.background.image',
                });
            },
        },

        onboarding: {
            update: this.put(PATHS.onboarding),
        },

        collections: {
            list: this.get(PATHS.collections),
            /** @member {ApiPutMethod<{}, ProductCollection>} */
            create: this.post(PATHS.collections),
            retrieve: this.get(`${PATHS.collections}/:collectionId`),
            sort: this.put(`${PATHS.collections}/sort`),
            /** @member {ApiPutMethod<{ collectionId: string }, ProductCollection>} */
            update: this.put(`${PATHS.collections}/:collectionId`),
            /** @member {ApiDeleteMethod<{ collectionId: string }>} */
            delete: this.delete(`${PATHS.collections}/:collectionId`),
            products: {
                get: this.get(`${PATHS.collections}/:collectionId/products`),
                update: this.put(`${PATHS.collections}/:collectionId/products`),
            },
        },

        contacts: {
            list: this.get(PATHS.contacts),
            update: this.put(`${PATHS.contacts}/:contactId`),
            delete: this.delete(`${PATHS.contacts}/:contactId`),
        },

        conversations: {
            /** @member {ApiGetMethod<{ next?: string }, { next?: string, assign: string, status: string, sort: string, searchValue: string }>} */
            list: this.get(PATHS.conversations),
            retrieve: this.get(`${PATHS.conversations}/:contactId`),
            /** @member {ApiPutMethod<{ contactId: string }>} */
            read: this.put(`${PATHS.conversations}/:contactId/read`),
            /** @member {ApiPutMethod<{ contactId: string, userId: string }>} */
            assign: this.put(`${PATHS.conversations}/:contactId/assign/:userId`),
            /** @member {ApiPutMethod<{ contactId: string }>} */
            assignMe: this.put(`${PATHS.conversations}/:contactId/assign/me`),
            /** @member {ApiPutMethod<{ contactId: string, status: 'open'|'close' }>} */
            changeStatus: this.put(`${PATHS.conversations}/:contactId/status/:status`),
            /** @member {ApiPutMethod<{ contactId: string }>} */
            block: this.put(`${PATHS.conversations}/:contactId/block`),
            uploadAttachment: async ({ payload, contactId }) => {
                const form = new FormData();
                form.append('file', payload);

                return this.apiCall('POST', `${PATHS.conversations}/:contactId/uploads/attachment`, {
                    contactId,
                    payload: form,
                    query: { field: 'file' },
                });
            },
            sendAttachment: async ({ contactId, payload }) => {
                const { file, ...rest } = payload;
                const { url, filename, mime, size } = await this.resources.conversations.uploadAttachment({
                    contactId,
                    payload: file,
                });

                return this.resources.messages.create({
                    contactId,
                    payload: {
                        type: mime === 'image/png' || mime === 'image/jpeg' ? 'image' : 'file',
                        mediaUrl: url,
                        fileName: filename,
                        fileSize: size,
                        destination: 'client',
                        ...rest,
                    },
                });
            },
            /** @member {ApiPutMethod<{ contactId: string }, { text: string }>} */
            sendMessage: ({ contactId, payload }) => {
                return this.apiCall('POST', PATHS.messages, {
                    contactId,
                    payload: { type: 'text', destination: 'client', ...payload },
                });
            },
            messages: this.get(PATHS.messages),
            /** @member {ApiGetMethod<{}, { status: string }>} */
            statistics: this.get(`${PATHS.conversations}/statistics`),
            unread: this.get(`${PATHS.conversations}/unread`),
        },

        integrations: {
            create: this.post(PATHS.integrations),
            /** @member {ApiDeleteMethod<{ integrationId: string }>} */
            delete: this.delete(`${PATHS.integrations}/:integrationId`),
        },

        messages: {
            list: this.get(PATHS.messages),
            create: this.post(PATHS.messages),
        },

        products: {
            list: this.get(PATHS.products),
            sublist: this.get(PATHS.products),
            create: this.post(PATHS.products),
            retrieve: this.get(`${PATHS.products}/:productId`),
            update: this.put(`${PATHS.products}/:productId`),
            delete: this.delete(`${PATHS.products}/:productId`),
            sort: this.put(`${PATHS.products}/sort`),
            migrate: this.post(`${PATHS.products}/migrate`),
            /** @member {ApiGetMethod<{ collectionId: string }, { next?: string, filter?: string }, { next?: string, results: *[] }>} load */
            listByCollection: this.get(`${PATHS.products}/:collectionId/sorted`),
            retrieveBySlug: this.get(`${PATHS.products}/:collectionId/:productSlug`),
            uploadPhoto: ({ payload, websiteId }) => {
                return media.uploadImage({
                    file: payload,
                    workspace: this.getWorkspaceId(),
                    website: websiteId,
                    destination: `w/${this.getWorkspaceId()}/websites/${websiteId}/products`,
                });
            },
            uploadPhotos: async ({ payloads, websiteId }) => {
                const results = await Promise.all(
                    payloads.map(async (file) => {
                        try {
                            const { url } = await this.resources.products.uploadPhoto({
                                payload: file,
                                websiteId,
                            });

                            return { file: file.name, result: url };
                        } catch (error) {
                            return { file: file.name, error: (error.errors && error.errors.photos) || error.message };
                        }
                    })
                );
                const success = results.filter(({ result }) => result);
                const failed = results.filter(({ error }) => error);

                return {
                    success,
                    failed,
                };
            },
        },

        replies: {
            list: this.get(PATHS.replies),
            create: this.post(PATHS.replies),
            update: this.put(`${PATHS.replies}/:replyId`),
            delete: this.delete(`${PATHS.replies}/:replyId`),
        },

        reviews: {
            list: this.get(PATHS.reviews),
            create: this.post(PATHS.reviews),
            delete: this.delete(`${PATHS.reviews}/:reviewId`),
            my: this.get(`${PATHS.reviews}/my`),
        },

        team: {
            create: this.post(PATHS.team),
            update: this.put(`${PATHS.team}/:memberId`),
            delete: this.delete(`${PATHS.team}/:memberId`),
        },

        users: {
            create: async (options) => {
                const res = await this.apiCall('POST', `${PATHS.users}`, options);
                this.accessToken = res.token;
                return res;
            },
            // validate: this.post(`${PATHS.users}/validate`),
            // validateCode: this.post(`${PATHS.users}/validate/code`),
            // validateEmail: this.post(`${PATHS.users}/validate/email`),
            // validateUrl: this.post(`${PATHS.users}/validate/url`),
            // validatePassword: this.post(`${PATHS.users}/validate/password`),
            checkInvite: this.post(`${PATHS.users}/check-invite`),
            checkRegistrationInvite: this.post(`${PATHS.users}/check-reg-invite`),
            recoveryPassword: this.post(`${PATHS.users}/recovery-password`),
            join: async (options) => {
                const res = await this.apiCall('POST', `${PATHS.users}/join`, options);
                this.accessToken = res.token;
                return res;
            },
            me: this.get(`${PATHS.users}/me`),
            updateMyAccount: this.put(`${PATHS.users}/me`),
            changeMyPassword: this.put(`${PATHS.users}/me/change-password`),
            sendFeedback: this.put(`${PATHS.users}/me/feedback`),
            saveMySubscription: this.put(`${PATHS.users}/me/save-subscription`),
            /** @member {ApiPutMethod<{}, Record<string, string>, void>} */
            updateUtm: this.post(`${PATHS.users}/me/utm`),
            uploadAvatar: ({ payload, field }) => {
                return this.resources.workspaces.upload({
                    uploadType: 'photo',
                    payload,
                    field,
                });
            },
            invites: {
                list: this.get(PATHS.invites),
                create: this.post(PATHS.invites),
                delete: this.delete(`${PATHS.invites}/:inviteId`),
            },
            switchApp: this.put(`${PATHS.users}/me/switch-app`),
            connectTelegram: this.post(`${PATHS.users}/me/connect-telegram`),
        },

        workspaces: {
            my: this.get(`${PATHS.workspaces}/my`),
            create: this.post(PATHS.workspaces),
            retrieve: this.get(`${PATHS.workspaces}/:workspaceId`),
            update: this.put(`${PATHS.workspaces}/:workspaceId`),
            delete: this.delete(`${PATHS.workspaces}/:workspaceId`),
            upload: async ({ payload, field, uploadType }) => {
                const form = new FormData();
                form.append(field, payload);

                return this.apiCall('POST', `${PATHS.workspaces}/:workspaceId/uploads/:uploadType`, {
                    payload: form,
                    query: { field },
                    uploadType,
                });
            },
        },

        websites: {
            list: this.method('GET', PATHS.websites),
            create: this.method('POST', PATHS.websites),
            scaffold: this.method('POST', `${PATHS.websites}/scaffold`),
            retrieve: this.method('GET', `${PATHS.websites}/:websiteId`),
            update: this.method('PUT', `${PATHS.websites}/:websiteId`),
            delete: this.method('DELETE', `${PATHS.websites}/:websiteId`),
            retrieveBySlug: this.method('GET', `${PATHS.websites}/slug/:websiteSlug`),
            domain: {
                update: this.method('PUT', `${PATHS.websites}/:websiteId/domain`),
                delete: this.method('DELETE', `${PATHS.websites}/:websiteId/domain`),
                get: this.method('GET', `${PATHS.websites}/:websiteId/domain`),
            },
            upload: async ({ websiteId, payload }) => {
                return media.uploadImage({
                    file: payload,
                    workspace: this.getWorkspaceId(),
                    website: websiteId,
                    destination: `w/${this.getWorkspaceId()}/websites/${websiteId}`,
                });
            },
            uploadFavicon: async ({ websiteId, payload }) => {
                return media.uploadIcon({
                    file: payload,
                    workspace: this.getWorkspaceId(),
                    website: websiteId,
                    destination: `w/${this.getWorkspaceId()}/websites/${websiteId}`,
                });
            },
            uploadMany: async ({ websiteId, payloads, field = 'files' }) => {
                const results = await Promise.all(
                    payloads.map(async (file) => {
                        try {
                            const result = await this.resources.websites.upload({
                                websiteId,
                                field,
                                payload: file,
                            });

                            return { file: file.name, result };
                        } catch (error) {
                            return { file: file.name, error: (error.errors && error.errors[field]) || error.message };
                        }
                    })
                );
                const success = results.filter(({ result }) => result);
                const failed = results.filter(({ error }) => error);

                return {
                    success,
                    failed,
                };
            },
            deleteUpload: async ({ path }) => media.deleteFiles({ path: [path] }),
            crm: {
                settings: {
                    update: this.post(`${PATHS.websites}/:websiteId/crm/settings`),
                },
            },
            audience: {
                /** @member {ApiGetMethod<{ websiteId: string }, { next: string }, Array<*>>} */
                list: this.get(`${PATHS.websites}/:websiteId/audience`),
                /** @member {ApiGetMethod<{ websiteId: string }, {}, void>} */
                download: async ({ websiteId }) =>
                    this.apiCall('GET', `${PATHS.websites}/:websiteId/audience/download`, {
                        websiteId,
                        raw: true,
                    }),
            },
            services: {
                /** @member {ApiPutMethod<{ websiteId: string }, { message: string }>} */
                requestABlock: this.post(`${PATHS.websites}/:websiteId/request-a-block`),
            },
        },

        pages: {
            list: this.method('GET', PATHS.pages),
            create: this.method('POST', PATHS.pages),
            retrieve: this.method('GET', `${PATHS.pages}/:pageId`),
            update: this.method('PUT', `${PATHS.pages}/:pageId`),
            delete: this.method('DELETE', `${PATHS.pages}/:pageId`),
            retrieveBySlug: this.method('GET', `${PATHS.pages}/slug/:pageSlug`),
            /** @member {ApiGetMethod<{ websiteId: string, pageId: string }, { from: string, to: string }, Array<{  }>>} */
            statistics: this.get(`${PATHS.pages}/:pageId/statistics`),
            /** @member {ApiPutMethod<{ websiteId: string, pageId: string, templateId: string }>} */
            applyTemplate: this.post(`${PATHS.pages}/:pageId/apply-template/:templateId`),
        },

        feedbacks: {
            list: this.method('GET', PATHS.feedbacks),
            create: this.method('POST', PATHS.feedbacks),
            /** @member {ApiGetMethod<{ websiteId: string }, {}, FeedbackCounts>} */
            count: this.method('GET', `${PATHS.feedbacks}/count`),
            /** @member {ApiPutMethod<{}, { websiteIds: string[]; types?: Array<'new'|'processing'|'rejected'|'accepted'> }, Record.<string, FeedbackCounts>>} */
            perWebsiteCount: this.post(`${PATHS.websites}/feedback/count`),
            /** @member {ApiGetMethod<{ websiteId: string }, {}, { new: number }>} */
            recent: this.method('GET', `${PATHS.feedbacks}/recent`),
            /** @member {ApiGetMethod<{ websiteId: string, feedbackId: string }, {}, { _id: string }>} */
            retrieve: this.method('GET', `${PATHS.feedbacks}/:feedbackId`),
            update: this.method('PUT', `${PATHS.feedbacks}/:feedbackId`),
            delete: this.method('DELETE', `${PATHS.feedbacks}/:feedbackId`),
        },

        orders: {
            list: this.method('GET', PATHS.orders),
            update: this.method('PUT', `${PATHS.orders}/:orderId`),
        },

        blocks: {
            create: this.post(PATHS.blocks),
            /** @member {ApiPutMethod<{ websiteId: string, pageId: string, blockId: string }, { blocks: Array<*>}>} */
            update: this.put(`${PATHS.blocks}/:blockId`),
            /** @member {ApiPutMethod<{ websiteId: string, pageId: string, blockId: string }, { to: string }>} */
            convert: this.put(`${PATHS.blocks}/:blockId/convert`),
            /** @member {ApiDeleteMethod<{ websiteId: string, pageId: string, blockId: string }>} */
            delete: this.delete(`${PATHS.blocks}/:blockId`),
            /** @member {ApiPutMethod<{ websiteId: string, pageId: string }, { blockId: string[], enable: boolean }>} */
            toggle: this.put(`${PATHS.blocks}/toggle`),
            /** @member {ApiPutMethod<{ websiteId: string, pageId: string }, { blockId: string[], group: boolean }>} */
            group: this.put(`${PATHS.blocks}/group`),
            /** @member {ApiPutMethod<{ websiteId: string, pageId: string }, { blockId: string[] }>} */
            clone: this.put(`${PATHS.blocks}/clone`),
            /** @member {ApiPutMethod<{ websiteId: string, pageId: string }, { blockId: string[] }>} */
            deleteMany: this.put(`${PATHS.blocks}/deleteMany`),
            /** @member {ApiPutMethod<{ websiteId: string, pageId: string }, { oldIndex: number, newIndex: number }>} */
            sort: this.put(`${PATHS.blocks}/sort`),
        },

        tags: {
            list: this.get(PATHS.tags),
            create: this.post(PATHS.tags),
            retrieve: this.get(`${PATHS.tags}/:tagId`),
            update: this.put(`${PATHS.tags}/:tagId`),
            delete: this.delete(`${PATHS.tags}/:tagId`),
        },

        pageTemplates: {
            /** @member {ApiGetMethod<{}, { title?: string, primaryTags: string[], secondaryTags: string[] }, { results: Array<{ name: string, blocks: Array }>, next?: string }>} */
            list: this.get(PATHS.templates),
            /** @member {ApiPutMethod<{}, { public: boolean, name: string, primaryTags: string[], secondaryTags: string[], blocks: Array }>} */
            create: this.post(PATHS.templates),
            /** @member {ApiGetMethod<{ templateId: string }, {}, { _id: string, name: string, blocks: Array }>} */
            retrieve: this.get(`${PATHS.templates}/:templateId`),
            /** @member {ApiPutMethod<{ templateId: string }, { name: string, primaryTags: string[], secondaryTags: string[], blocks: Array }>} */
            update: this.put(`${PATHS.templates}/:templateId`),
            /** @member {ApiDeleteMethod<{ templateId: string }>} */
            delete: this.delete(`${PATHS.templates}/:templateId`),
            uploadThumbnail: ({ payload, field }) => {
                return this.resources.workspaces.upload({
                    uploadType: 'template',
                    payload,
                    field,
                });
            },
        },

        crm: {
            settings: {
                update: this.post(`${PATHS.crm}/settings`),
            },
        },

        endpoints: {
            /** @member {ApiGetMethod<{}, { withPurchase: boolean }, { status: 'ok'|'error' }>} */
            sample: this.get(`${PATHS.endpoints}/test`),
            /** @member {ApiPutMethod<{}, { endpoint: string; feedback: object }, { status: 'ok'|'error' }>} */
            test: this.post(`${PATHS.endpoints}/test`),
        },
    };
}
